import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import {
  PartnersLink,
  PartnersMonitor,
  PartnersTeam
} from "@dreamhub/designsystem/icons";
import {
  useAccountSettings,
  useUpdateAccountSettingByKey
} from "@/entities/settings/hooks";
import { extractKeyValuePairs } from "@/pages/settings/pages/GeneralSettings";
import EnablePartnersModal from "@/pages/settings/components/partnersSettings/EnablePartnersModal";
import DisablePartnersModal from "@/pages/settings/components/partnersSettings/DisablePartnersModal";
import PartnershipEnable from "@/assets/icons/PartnershipEnable.png";

const TRANSLATION_KEY = "settings.partners.generalTab.partnerPortal";
const ENABLE_PARTNERSHIPS_KEY = "enable_partnerships";

export const GeneralTab: React.FC = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);

  const accountSettingsQuery = useAccountSettings();
  const accountSettings = accountSettingsQuery.data;
  const updateSettingsByKey = useUpdateAccountSettingByKey();

  const keyValuePairs = accountSettings
    ? extractKeyValuePairs(accountSettings)
    : {};
  const isEnabled = keyValuePairs?.[ENABLE_PARTNERSHIPS_KEY] ?? false;

  const setEnabled = (value: boolean) => {
    updateSettingsByKey.mutate({
      pathParams: { settingKey: ENABLE_PARTNERSHIPS_KEY },
      data: { value }
    });
  };

  const handleCheckedChange = (checked: boolean) => {
    if (checked) {
      setIsModalOpen(true);
    } else {
      setIsDisableModalOpen(true);
    }
  };

  const highlights = [
    { icon: <PartnersTeam />, text: t(`${TRANSLATION_KEY}.highlights.manage`) },
    { icon: <PartnersLink />, text: t(`${TRANSLATION_KEY}.highlights.link`) },
    {
      icon: <PartnersMonitor />,
      text: t(`${TRANSLATION_KEY}.highlights.monitor`)
    }
  ];

  return (
    <>
      <div className="flex w-full flex-col rounded-2xl overflow-hidden border border-border-border1 bg-[#101724]">
        <div className="flex flex-col gap-1 p-4">
          <div className="flex flex-row items-center justify-between gap-4">
            <Text variant="label">{t(`${TRANSLATION_KEY}.title`)}</Text>
            <Switch
              checked={isEnabled}
              onCheckedChange={handleCheckedChange}
              data-test="partners-enable-toggle"
            />
          </div>
          <Text variant="bodySecondary" className="max-w-[640px]">
            {t(`${TRANSLATION_KEY}.subTitle`)}
          </Text>
        </div>
        {!isEnabled ? (
          <div className="flex flex-row items-stretch border-t border-border-border1">
            <div className="flex flex-1 flex-col gap-6 p-8 pt-11">
              {highlights.map((highlight, index) => (
                <div key={index} className="flex flex-row items-start gap-3">
                  <IconContainer size="5" className="mt-0.5 shrink-0">
                    {highlight.icon}
                  </IconContainer>
                  <Text variant="body">{highlight.text}</Text>
                </div>
              ))}
            </div>
            <div className="w-[396px] max-w-full self-center">
              <img src={PartnershipEnable} alt="" className="h-auto w-full" />
            </div>
          </div>
        ) : null}
      </div>
      <EnablePartnersModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        onConfirm={() => setEnabled(true)}
      />
      <DisablePartnersModal
        isOpen={isDisableModalOpen}
        setIsOpen={setIsDisableModalOpen}
        onConfirm={() => setEnabled(false)}
      />
    </>
  );
};
